import { createContext, type ReactElement, use, useMemo } from 'react';
import {
  type FieldValues,
  FormProvider,
  type FormProviderProps,
  type SubmitHandler,
  useFormContext,
} from 'react-hook-form';
import { type ObjectSchema } from 'yup';

interface FormProviderKnowMeProps {
  validation?: ObjectSchema<object>;
}

type Props<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues extends FieldValues | undefined = undefined,
> = FormProviderProps<TFieldValues, TContext, TTransformedValues> & {
  formProps?: Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'>;
  onSubmit?: SubmitHandler<TFieldValues>;
  validation: ObjectSchema<object>;
};

const FormProviderKnowMeDefaultValues: FormProviderKnowMeProps = {
  validation: undefined,
};
const FormProviderKnowMePropsContext = createContext<FormProviderKnowMeProps>(
  FormProviderKnowMeDefaultValues,
);

export const useFormProviderKnowMePropsContext = (): FormProviderKnowMeProps => {
  return use<FormProviderKnowMeProps>(FormProviderKnowMePropsContext);
};

function FormWrapper<TFieldValues extends FieldValues>({
  children,
  formProps,
  onSubmit,
}: Readonly<{
  children: React.ReactNode;
  formProps?: Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'>;
  onSubmit: SubmitHandler<TFieldValues>;
}>) {
  const { handleSubmit } = useFormContext<TFieldValues>();
  return (
    <form {...formProps} onSubmit={handleSubmit(onSubmit)}>
      {children}
    </form>
  );
}

export const FormProviderKnowMe = <
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues extends FieldValues | undefined = undefined,
>(
  props: Props<TFieldValues, TContext, TTransformedValues>,
): ReactElement<Props<TFieldValues, TContext, TTransformedValues>> => {
  const { formProps, onSubmit, validation, ...rest } = props;
  const memoValidation = useMemo(
    () => ({
      validation,
    }),
    [validation],
  );

  return (
    <FormProvider {...rest}>
      <FormProviderKnowMePropsContext value={memoValidation}>
        {onSubmit ? (
          <FormWrapper<TFieldValues> formProps={formProps} onSubmit={onSubmit}>
            {props.children}
          </FormWrapper>
        ) : (
          props.children
        )}
      </FormProviderKnowMePropsContext>
    </FormProvider>
  );
};
